<template>
  <div class="excel-table-wrapper" :id="tableKey">
    <vxe-grid
      ref="table"
      size="mini"
      border
      resizable
      auto-resize
      highlight-hover-row
      show-overflow
      show-header-overflow
      :columns="tableColumns"
      :data="data"
      :loading="loading"
      :height="height"
      :toolbar-config="{ zoom: true, slots: { buttons: 'toolbar_form' } }"
      :seq-config="{ startIndex: 1 }"
      @paste="pasteFile"
    >
      <template #toolbar_form>
        <div class="d-flex align-items-center">
          <div :class="themeType ? `side-frame-${themeType}` : 'side-frame'">
            {{ title }}
          </div>
          <el-divider
            v-if="fileName"
            direction="vertical"
            class="mx-2"
          ></el-divider>
          <div v-if="fileName" class="fileName">
            {{ fileName }}
          </div>

          <el-upload
            class="top-upload ml-3"
            ref="topUpload"
            v-bind="uploadOpts"
            :drag="false"
            :on-change="uploadChange"
          >
            <b-button
              size="sm"
              :variant="!themeType ? 'outline-primary' : `outline-${themeType}`"
            >
              选择文件
            </b-button>
          </el-upload>
          <el-checkbox
            v-model="skipHidden"
            size="small"
            class="ml-2"
            border
            @change="skipHiddenChange"
          >
            跳过隐藏列/行
          </el-checkbox>
        </div>
      </template>
      <template #empty="{}">
        <el-upload
          ref="dragUpload"
          v-if="!loading"
          v-bind="uploadOpts"
          :on-change="uploadChange"
        >
          <template #trigger>
            <div @click.stop class="trigger">
              <div>
                <i class="el-icon-upload"></i>
                <div class="el-upload__text mt-2">
                  将文件拖到此处，或<em>复制上传</em>、<span
                    @click="clickUpload"
                    class="click-upload"
                    ><em>点击上传</em></span
                  >
                </div>
              </div>
            </div>
          </template>
        </el-upload>
      </template>
    </vxe-grid>
  </div>
</template>
<script>
export default {
  props: {
    columns: { type: Array, default: () => [] },
    data: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    height: { type: Number, default: 0 },
    title: { type: String, default: '文件列表' },
    themeType: { type: String, default: '' },
    tableKey: { type: String, default: '' },
  },
  data() {
    return {
      uploadOpts: {
        drag: true,
        action: '#',
        accept: '.xlsx,.xlsm,.csv',
        'auto-upload': false,
        'show-file-list': false,
      },
      fileValue: null,
      skipHidden: false,
    }
  },
  computed: {
    tableColumns() {
      if (!this.columns || !this.columns.length) return []
      const _columns = this.columns.map(({ label, field }) => ({
        title: label,
        field,
        minWidth: 140,
        align: 'left',
        headerAlign: 'center',
        showHeaderOverflow: true,
        showOverflow: true,
      }))
      _columns.push({
        title: '1',
        field: 'seq',
        width: 50,
        align: 'center',
        fixed: 'left',
        type: 'seq',
      })
      return _columns
    },
    isEmpty() {
      return !this.data || !this.data.length
    },
    fileName() {
      if (!this.fileValue) return
      if (!this.tableColumns || !this.tableColumns.length) return
      return this.fileValue.name
    },
  },
  mounted() {
    const table = document.querySelector(
      `#${this.tableKey}.excel-table-wrapper`
    )
    table.addEventListener('drop', (e) => {
      if (!this.isEmpty) {
        this.fileUpload(e.dataTransfer.files[0])
      }
      e.preventDefault()
      e.stopPropagation()
    })
    document.addEventListener(
      'dragover',
      (e) => {
        e.preventDefault()
        e.stopPropagation()
      },
      false
    )
    table.addEventListener(
      'dragleave',
      (e) => {
        e.preventDefault()
        e.stopPropagation()
      },
      false
    )
    table.addEventListener(
      'dragenter',
      (e) => {
        e.preventDefault()
        e.stopPropagation()
      },
      false
    )
  },
  methods: {
    skipHiddenChange(e) {
      this.$emit('upload-change', this.fileValue, e)
    },
    uploadChange(file) {
      this.fileUpload(file.raw)
      if (this.$refs.topUpload) this.$refs.topUpload.clearFiles()
      if (this.$refs.dragUpload) this.$refs.dragUpload.clearFiles()
    },
    async pasteFile({ $event }) {
      const { items } = $event.clipboardData || window.clipboardData
      if (!items || !items.length) {
        return this.$message.error('当前浏览器不支持')
      }

      let file
      for (let i = 0; i < items.length; i++) {
        const f = items[i].getAsFile()
        if (f) {
          file = f
          break
        }
      }
      this.fileUpload(file)
    },
    async clickUpload() {
      const res = await this.$refs.table.readFile({
        types: ['xlsx', 'xlsm', 'csv'],
      })
      if (!res) return
      const { file, status } = res
      if (!file || !status) return
      this.fileUpload(file)
    },
    fileUpload(file) {
      if (!file) return

      const fileName = file.name

      const fileType = fileName.substring(fileName.lastIndexOf('.'))
      const { accept } = this.uploadOpts
      if (accept) {
        const types = accept.split(',')
        if (!types.includes(fileType)) {
          return this.$message.error('该类型的文件不支持比对')
        }
      }
      this.fileValue = file

      this.$emit('upload-change', file, this.skipHidden)
    },
  },
}
</script>
<style lang="scss" scoped>
div[class^='side-frame'] {
  margin: 0;
  padding-right: 0;
  display: inline-block;
  font-size: 14px;
  color: #606266;
}
.fileName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: #606266;
  max-width: 30%;
}
::v-deep {
  .vxe-table--empty-content {
    width: 100%;
    height: 100%;
    display: flex !important;
    align-items: center;
    justify-content: center;
    > div {
      width: 100%;
      height: 100%;
    }
    .el-upload {
      width: 100%;
      height: 100%;
      cursor: auto;
      .el-upload-dragger {
        width: 100%;
        height: 100%;
        border: none;
        .trigger {
          width: 100%;
          height: 100%;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: auto;
          .click-upload {
            cursor: pointer;
          }
        }
        .el-icon-upload {
          margin: 0;
        }
      }
      .el-upload__tip {
        line-height: 1.6;
      }
    }
  }
}
</style>
